import { FC, useState, useRef, useEffect } from 'react'
import { Modal } from '../../components/modal/modal'
import { Checkbox } from '../../components/checkbox/checkbox'
import { CustomButton } from '../../components/customButton'
import { PortalTooltip } from '../../components/tooltip/portalTooltip'
import { useInputSanitize, DISPLAY_NAME_PATTERN } from '../../hooks/useInputSanitize'
import { useDispatch, useSelector } from '../../hooks/redux'
import { createVoiceGroup, getVoiceGroups } from '../../store/slices/voiceGroups/async'
import classes from '../../styles/voices/groupAccessModal.module.scss'

interface CreateGroupModalProps {
  onClose: () => void
}

export const CreateGroupModal: FC<CreateGroupModalProps> = ({ onClose }) => {
  const dispatch = useDispatch()
  const users = useSelector((state) => state.user.users)
  const { value: groupName, onChange: onGroupNameChange, onBlur: onGroupNameBlur } = useInputSanitize('', { maxLength: 50, allowPattern: DISPLAY_NAME_PATTERN })
  const [selected, setSelected] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleCreate = async () => {
    if (!groupName.trim()) return
    await dispatch(createVoiceGroup({ name: groupName.trim(), is_default: false, voices: [], users: selected.map((username) => { const u = users.find((u) => u.auth_username === username); return { user_id: u?.user_id ?? 0, username } }) }))
    await dispatch(getVoiceGroups())
    onClose()
  }

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const toggle = (user: string) => {
    setSelected((prev) =>
      prev.includes(user) ? prev.filter((u) => u !== user) : [...prev, user]
    )
  }

  return (
    <Modal isOpen title="Создать группу" handleClose={onClose} classes={{ modalContent: classes.modal }}>
      <div className={classes.wrapper}>
        <div className={classes.inputWrap}>
          <input
            className={classes.input}
            placeholder="Имя группы"
            value={groupName}
            onChange={onGroupNameChange}
            onBlur={onGroupNameBlur}
          />
        </div>

        <div className={classes.dropdown} ref={wrapperRef}>
          <PortalTooltip
            placement="bottom-middle"
            tooltipContent={selected.length > 0 ? selected.join(', ') : null}
            classes={{ contentClassName: classes.tooltip }}
          >
            <div className={classes.inputWrap}>
              <input
                className={classes.input}
                placeholder="Введите имя пользователя"
                value={open ? search : selected.length > 0 ? selected.join(', ') : ''}
                onChange={(e) => { setSearch(e.target.value); setOpen(true) }}
                onFocus={() => { setOpen(true); setSearch('') }}
              />
            </div>
          </PortalTooltip>
          {open && (
            <div className={classes.userList}>
              {users.filter((u) => u.auth_username.toLowerCase().includes(search.toLowerCase())).map((user, i) => (
                <div
                  key={i}
                  className={`${classes.userItem} ${selected.includes(user.auth_username) ? classes.userItemSelected : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); toggle(user.auth_username) }}
                >
                  <Checkbox
                    isChecked={selected.includes(user.auth_username)}
                    handleCheckboxChange={() => {}}
                  />
                  <span className={classes.userName}>{user.auth_username}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={classes.footer}>
          <CustomButton className={classes.footerCancel} onClick={onClose}>Отменить</CustomButton>
          <CustomButton className={classes.footerApply} onClick={handleCreate} disabled={!groupName.trim()}>Создать</CustomButton>
        </div>
      </div>
    </Modal>
  )
}
